import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Paper,
} from '@mui/material';
import {
  Check,
  ShoppingCart,
  Email,
} from '@mui/icons-material';
import {
  getCheckoutLinkInfo,
  createCheckoutSession,
  registerFreePlan,
  CheckoutLinkInfo,
} from '../../services/api/checkoutService';

const CheckoutPage = () => {
  const { '*': slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkInfo, setLinkInfo] = useState<CheckoutLinkInfo | null>(null);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadLinkInfo(slug);
    }
  }, [slug]);

  const loadLinkInfo = async (linkSlug: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCheckoutLinkInfo(linkSlug);
      setLinkInfo(data);
    } catch (err: any) {
      console.error('Link info error:', err);
      setError(err.response?.data?.detail || '決済リンクが見つかりません');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async () => {
    if (!linkInfo) return;

    // バリデーション
    if (!email) {
      setEmailError('メールアドレスを入力してください');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('正しいメールアドレスを入力してください');
      return;
    }
    setEmailError(null);

    setSubmitting(true);
    setError(null);

    try {
      if (linkInfo.is_free) {
        // 無料プラン登録
        const result = await registerFreePlan({
          payment_link_id: linkInfo.link_id,
          email,
          full_name: fullName || undefined,
        });
        if (result.success) {
          navigate('/checkout/success?type=free');
        }
      } else {
        // 有料: Stripeへリダイレクト
        const successUrl = `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`;
        const cancelUrl = window.location.href;

        const result = await createCheckoutSession({
          payment_link_id: linkInfo.link_id,
          email,
          success_url: successUrl,
          cancel_url: cancelUrl,
        });

        // Stripeチェックアウトへリダイレクト
        window.location.href = result.checkout_url;
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || '処理に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !linkInfo) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'grey.50',
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!linkInfo) return null;

  const primaryColor = linkInfo.tenant.branding.primary_color || '#1976d2';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        {/* ヘッダー: ブランドロゴ/名前 */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {linkInfo.tenant.branding.logo_url ? (
            <img
              src={linkInfo.tenant.branding.logo_url}
              alt={linkInfo.tenant.branding.brand_name}
              style={{ maxHeight: 60, maxWidth: '100%' }}
            />
          ) : (
            <Typography
              variant="h5"
              sx={{ color: primaryColor, fontWeight: 'bold' }}
            >
              {linkInfo.tenant.branding.brand_name}
            </Typography>
          )}
        </Box>

        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* 左側: 商品情報 */}
            <Box
              sx={{
                flex: 1,
                p: 4,
                bgcolor: 'grey.100',
              }}
            >
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {linkInfo.product.name}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {linkInfo.plan.name}プラン
              </Typography>

              {linkInfo.product.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {linkInfo.product.description}
                </Typography>
              )}

              {linkInfo.plan.features.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    含まれる機能
                  </Typography>
                  <List dense>
                    {linkInfo.plan.features.map((feature, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Check sx={{ color: 'success.main', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h3" fontWeight="bold" sx={{ color: primaryColor }}>
                  {linkInfo.is_free ? '無料' : `¥${linkInfo.price.toLocaleString()}`}
                </Typography>
                {!linkInfo.is_free && (
                  <Typography variant="body2" color="text.secondary">
                    /月
                  </Typography>
                )}
              </Box>
            </Box>

            {/* 右側: フォーム */}
            <Box
              sx={{
                flex: 1,
                p: 4,
              }}
            >
              <Typography variant="h6" gutterBottom>
                {linkInfo.is_free ? '無料で始める' : 'ご購入手続き'}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  label="メールアドレス"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!emailError}
                  helperText={emailError}
                  InputProps={{
                    startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  sx={{ mb: 2 }}
                />

                {linkInfo.is_free && (
                  <TextField
                    fullWidth
                    label="お名前（任意）"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : <ShoppingCart />}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    bgcolor: primaryColor,
                    '&:hover': {
                      bgcolor: primaryColor,
                      filter: 'brightness(0.9)',
                    },
                  }}
                >
                  {submitting
                    ? '処理中...'
                    : linkInfo.is_free
                    ? '無料で登録'
                    : '決済へ進む'}
                </Button>

                {!linkInfo.is_free && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 2, textAlign: 'center' }}
                  >
                    安全なStripe決済で処理されます
                  </Typography>
                )}
              </Box>

              {/* サポート情報 */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="body2" color="text.secondary">
                ご不明な点がございましたら、
                <a href={`mailto:${linkInfo.tenant.branding.support_email}`}>
                  {linkInfo.tenant.branding.support_email}
                </a>
                までお問い合わせください。
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CheckoutPage;
